import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import { getSecondaryCandidates } from 'api/userApi';

import CustomInput from 'components/common/CustomInput/CustomInput';
import NameInputBox from 'components/common/NameInputBox';

import styles from './MemberSearchPicker.module.css';

// Best-effort split of a free-typed name into first/last for the create path. The user can correct it —
// this only saves them re-typing. Last whitespace-separated token becomes the surname.
const splitName = (raw) => {
  const parts = (raw || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1],
  };
};

const portalStatusLabel = (s) => {
  if (s === 'Accepted') return 'Accepted';
  if (s === 'Pending') return 'Pending';
  return 'Not invited';
};

// Club membership type for the row badge: guests read "Guest Member", everyone else shows their club role.
const membershipLabel = (role) => {
  if (!role) return 'Member';
  if (role === 'Guest') return 'Guest Member';
  return role;
};

const dupRelationship = (d) => {
  if (d.isMemberOfThisClub) {
    return d.thisClubRole
      ? `Member of this club (${d.thisClubRole})`
      : 'Member of this club';
  }
  return 'Not in this club';
};

const EMAIL_RE = /\S+@\S+\.\S+/;

/**
 * Reusable "search an existing club member, or create a new person" picker, laid out as a FIXED-SIZE region:
 * a fixed search/header row on top and a flex:1 panel below that scrolls internally. The dialog never resizes as
 * you type, select, or when the duplicate heads-up appears. See docs/secondary-contact-family-ui-plan.html §14.
 *
 * Emits the current selection via onChange:
 *   { type: 'existing', person, valid: true }
 *   { type: 'new', firstName, lastName, email, valid }
 *   null
 */
const MemberSearchPicker = ({
  clubId,
  primaryContactId,
  loadCandidates,
  excludeUserIds,
  eligibilityCheck,
  findDuplicates,
  duplicateBlockedReason,
  onChange,
  maxResults,
  createLabel,
  actionSlot,
}) => {
  const [directory, setDirectory] = useState([]);
  const [loadingDir, setLoadingDir] = useState(true);

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const [mode, setMode] = useState('search'); // 'search' | 'create'
  const [selected, setSelected] = useState(null);
  const [newPerson, setNewPerson] = useState({ firstName: '', lastName: '', email: '' });
  const [emailTouched, setEmailTouched] = useState(false);
  const [debouncedNewName, setDebouncedNewName] = useState({ firstName: '', lastName: '' });

  const [duplicates, setDuplicates] = useState([]);
  const suppressDupRef = useRef(null);

  // Load the directory once (members + guests), masked per viewer by the server. The source is pluggable via
  // loadCandidates (the add-member flow passes the club member list); without it we fall back to the family-scoped
  // secondary-candidates. Filtering — including the excludeUserIds exclusion — is applied in-memory at render time
  // (see `results`), so this fetch only re-runs when its inputs change, not on every render.
  useEffect(() => {
    let active = true;
    (async () => {
      setLoadingDir(true);
      try {
        const rows = loadCandidates
          ? (await loadCandidates()) || []
          : (await getSecondaryCandidates(clubId, primaryContactId))?.data || [];
        const seen = new Set();
        const deduped = [];
        for (const r of rows) {
          const id = r.userID || r.userId;
          if (!id || seen.has(id)) continue;
          seen.add(id);
          deduped.push(r);
        }
        if (active) setDirectory(deduped);
      } catch (e) {
        if (active) toast.error('Could not load club members to search');
      } finally {
        if (active) setLoadingDir(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [loadCandidates, clubId, primaryContactId]);

  // Debounce the query so filtering only runs after the user pauses.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim().toLowerCase()), 250);
    return () => clearTimeout(t);
  }, [query]);

  // Debounce the create-mode name so the duplicate check only fires after the user pauses.
  useEffect(() => {
    const t = setTimeout(
      () =>
        setDebouncedNewName({
          firstName: newPerson.firstName.trim(),
          lastName: newPerson.lastName.trim(),
        }),
      400
    );
    return () => clearTimeout(t);
  }, [newPerson.firstName, newPerson.lastName]);

  const results = useMemo(() => {
    if (debouncedQuery.length < 2) return [];
    const exclude = new Set(excludeUserIds || []);
    const q = debouncedQuery;
    const matches = directory.filter((p) => {
      const id = p.userID || p.userId;
      if (exclude.has(id)) return false;
      const name = `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase();
      const email = (p.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
    return matches.slice(0, maxResults);
  }, [debouncedQuery, directory, maxResults, excludeUserIds]);

  const evalEligibility = (p) =>
    eligibilityCheck ? eligibilityCheck(p) : { eligible: true };

  const newValid =
    !!newPerson.firstName.trim() &&
    !!newPerson.lastName.trim() &&
    EMAIL_RE.test(newPerson.email.trim());

  // Emit the current selection upward whenever it changes.
  useEffect(() => {
    if (selected) {
      onChange?.({ type: 'existing', person: selected, valid: true });
    } else if (mode === 'create') {
      onChange?.({
        type: 'new',
        firstName: newPerson.firstName.trim(),
        lastName: newPerson.lastName.trim(),
        email: newPerson.email.trim(),
        valid: newValid,
      });
    } else {
      onChange?.(null);
    }
  }, [selected, mode, newPerson, newValid, onChange]);

  // Duplicate check after selecting an existing person (other same-name people in this club; the server never lists
  // anyone outside it).
  useEffect(() => {
    if (!selected || !findDuplicates) return undefined;
    if (suppressDupRef.current && suppressDupRef.current === selected.userID) {
      return undefined;
    }
    let active = true;
    (async () => {
      const matches = await findDuplicates({
        firstName: selected.firstName,
        lastName: selected.lastName,
        excludeUserId: selected.userID,
      });
      if (active) setDuplicates(matches || []);
    })();
    return () => {
      active = false;
    };
  }, [selected, findDuplicates]);

  // Duplicate check while typing a new person.
  useEffect(() => {
    if (mode !== 'create' || !findDuplicates) return undefined;
    const { firstName, lastName } = debouncedNewName;
    if (!firstName || !lastName) {
      setDuplicates([]);
      return undefined;
    }
    let active = true;
    (async () => {
      const matches = await findDuplicates({ firstName, lastName });
      if (active) setDuplicates(matches || []);
    })();
    return () => {
      active = false;
    };
  }, [mode, debouncedNewName, findDuplicates]);

  const pickExisting = (p) => {
    if (!evalEligibility(p).eligible) return;
    suppressDupRef.current = null;
    setDuplicates([]);
    setSelected(p);
  };

  const startCreate = () => {
    const split = splitName(query);
    setNewPerson({ firstName: split.firstName, lastName: split.lastName, email: '' });
    setEmailTouched(false);
    setDuplicates([]);
    setSelected(null);
    setMode('create');
  };

  const clearSelection = () => {
    setSelected(null);
    setDuplicates([]);
    suppressDupRef.current = null;
    setQuery('');
    setDebouncedQuery('');
  };

  const backToSearch = () => {
    setMode('search');
    setNewPerson({ firstName: '', lastName: '', email: '' });
    setEmailTouched(false);
    setDuplicates([]);
  };

  // Switch the selection to a surfaced same-name account (attaches by id; the email may be masked).
  const switchTo = (dup) => {
    suppressDupRef.current = dup.userID;
    setMode('search');
    setSelected({
      userID: dup.userID,
      firstName: dup.firstName,
      lastName: dup.lastName,
      email: dup.email,
      role: dup.thisClubRole || '',
      invitationStatus: dup.emailConfirmed ? 'Accepted' : 'Not invited',
      isMemberOfThisClub: dup.isMemberOfThisClub,
    });
    setDuplicates([]);
  };

  const handleNewNameChange = (e) => {
    const { name, value } = e.target;
    setNewPerson((prev) => ({ ...prev, [name]: value }));
  };

  const handleNewEmailChange = (e) => {
    setNewPerson((prev) => ({ ...prev, email: e.target.value }));
  };

  const renderDuplicateBanner = () => {
    if (!duplicates || duplicates.length === 0) return null;
    const noun = duplicates.length === 1 ? 'account' : 'accounts';
    const verb = duplicates.length === 1 ? 'exists' : 'exist';
    return (
      <div className={styles.dupBanner}>
        <div className={styles.dupHeader}>
          <span>
            Heads up — {duplicates.length} other {noun} with this name {verb}:
          </span>
          <button
            type="button"
            className={styles.dupDismiss}
            onClick={() => setDuplicates([])}
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
        {duplicates.map((d) => {
          // A match the current flow can't attach (e.g. add-member: already in this club; add-secondary: already a
          // primary/secondary contact) shows a blocked reason instead of the "use this one instead" action.
          const blockedReason = duplicateBlockedReason ? duplicateBlockedReason(d) : null;
          return (
            <div key={d.userID} className={styles.dupRow}>
              <div className={styles.dupInfo}>
                <span className={styles.dupEmail}>{d.email}</span>
                <span className={styles.dupMeta}>
                  {portalStatusLabel(d.emailConfirmed ? 'Accepted' : 'Not invited')} ·{' '}
                  {dupRelationship(d)}
                </span>
              </div>
              {blockedReason ? (
                <span className={styles.rowBlocked}>{blockedReason}</span>
              ) : (
                <button type="button" className={styles.linkBtn} onClick={() => switchTo(d)}>
                  Use this one instead
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // --- Selected existing person ---
  if (selected) {
    const name = `${selected.firstName || ''} ${selected.lastName || ''}`.trim();
    const meta =
      selected.isMemberOfThisClub === false
        ? `Not in this club · ${portalStatusLabel(selected.invitationStatus)}`
        : `${membershipLabel(selected.role)} · ${portalStatusLabel(selected.invitationStatus)}`;
    return (
      <div className={styles.wrap}>
        <div className={styles.searchRow}>
          <div className={styles.selectedCard}>
            <div className={styles.selectedInfo}>
              <span className={styles.selectedName}>{name || '(no name)'}</span>
              <span className={styles.selectedEmail}>{selected.email}</span>
              <span className={styles.selectedMeta}>{meta}</span>
            </div>
            <button type="button" className={styles.linkBtn} onClick={clearSelection}>
              Change
            </button>
          </div>
          {actionSlot}
        </div>
        <div className={styles.panel}>{renderDuplicateBanner()}</div>
      </div>
    );
  }

  // --- Create a new person ---
  if (mode === 'create') {
    const emailError =
      emailTouched && !EMAIL_RE.test(newPerson.email.trim())
        ? 'Enter a valid email address'
        : '';
    return (
      <div className={styles.wrap}>
        <div className={styles.createHeader}>
          <span className={styles.createTitle}>{createLabel}</span>
          <button type="button" className={styles.linkBtn} onClick={backToSearch}>
            ← Search existing instead
          </button>
        </div>
        <div className={styles.panel}>
          <NameInputBox
            label="Contact Name"
            formValues={newPerson}
            errors={{}}
            handleInputChange={handleNewNameChange}
          />
          <CustomInput
            label="Email Address"
            name="email"
            value={newPerson.email}
            placeholder="contact@example.com"
            onChange={(e) => {
              setEmailTouched(true);
              handleNewEmailChange(e);
            }}
            error={emailError}
          />
          {renderDuplicateBanner()}
        </div>
        <div className={styles.createFooter}>{actionSlot}</div>
      </div>
    );
  }

  // --- Search ---
  return (
    <div className={styles.wrap}>
      <div className={styles.searchRow}>
        <div className={styles.searchBox}>
          <CustomInput
            label="Find a person"
            name="memberSearch"
            value={query}
            placeholder="Start typing a name or email…"
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div className={styles.searchAction}>{actionSlot}</div>
      </div>
      <div className={styles.panel}>
        {loadingDir && <div className={styles.note}>Loading club members…</div>}

        {!loadingDir && debouncedQuery.length < 2 && (
          <div className={styles.note}>Type at least 2 characters to search club members.</div>
        )}

        {!loadingDir && debouncedQuery.length >= 2 && results.length === 0 && (
          <div className={styles.note}>No matching club members.</div>
        )}

        {!loadingDir &&
          results.map((p) => {
            const id = p.userID || p.userId;
            const name = `${p.firstName || ''} ${p.lastName || ''}`.trim();
            const elig = evalEligibility(p);
            const isGuest = p.role === 'Guest';
            return (
              <div
                key={id}
                className={`${styles.row} ${elig.eligible ? '' : styles.rowDisabled}`}
                onClick={() => pickExisting(p)}
              >
                <div className={styles.rowTop}>
                  <div className={styles.rowNameWrap}>
                    <span className={styles.rowName}>{name || '(no name)'}</span>
                    <span
                      className={`${styles.typeBadge} ${isGuest ? styles.guestBadge : styles.memberBadge}`}
                    >
                      {membershipLabel(p.role)}
                    </span>
                  </div>
                  <span className={styles.rowEmail}>{p.email}</span>
                </div>
                {elig.eligible ? (
                  <span className={styles.rowMeta}>{portalStatusLabel(p.invitationStatus)}</span>
                ) : (
                  <span className={styles.rowBlocked}>Can’t add — {elig.reason}</span>
                )}
              </div>
            );
          })}

        {!loadingDir && (
          <div className={styles.addNewRow} onClick={startCreate}>
            + Not listed? Add a new person
          </div>
        )}
      </div>
    </div>
  );
};

MemberSearchPicker.propTypes = {
  clubId: PropTypes.string,
  // Family scope for the secondary-contact flow; omit when loadCandidates supplies the directory (add-member).
  primaryContactId: PropTypes.string,
  // Optional directory source: async () => rows[]. Falls back to family-scoped secondary-candidates when absent.
  loadCandidates: PropTypes.func,
  excludeUserIds: PropTypes.arrayOf(PropTypes.string),
  eligibilityCheck: PropTypes.func,
  findDuplicates: PropTypes.func,
  // (dup) => string|null — a reason makes the duplicate non-selectable (blocked label instead of "use this one").
  duplicateBlockedReason: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  maxResults: PropTypes.number,
  createLabel: PropTypes.string,
  actionSlot: PropTypes.node,
};

MemberSearchPicker.defaultProps = {
  clubId: null,
  primaryContactId: null,
  loadCandidates: null,
  excludeUserIds: [],
  eligibilityCheck: null,
  findDuplicates: null,
  duplicateBlockedReason: null,
  maxResults: 8,
  createLabel: 'Add a new person',
  actionSlot: null,
};

export default MemberSearchPicker;
